// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import { type TableViewId, type TableViewType, useEvolveDelete, useEvolveGet } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { GridApi } from 'ag-grid-enterprise';

import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { loadSavedGridState } from 'helpers/ag-grid/TableViewPanel/loadSavedGridState';
import type { TableSessionState } from 'helpers/ag-grid/TableViewPanel/types';
import { getTableSessionState, useTableSessionState } from 'helpers/ag-grid/TableViewPanel/useTableSessionState';
import { useTableViewChangeCheck } from 'helpers/ag-grid/TableViewPanel/useTableViewChangeCheck';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { parseUserPreferenceValueJson } from 'hooks/typeCacheHooks/useUserPreferences/utils';
import { getLocalStorageWrapped, setLocalStorageWrapped } from 'hooks/useLocalStorage';

/**
 * Hook to restore a table's grid state when it loads: the user's unsaved changes from earlier in their session if
 * there are any, and otherwise their default table view (with caching and background updates).
 * */
export const useInitialTableView = (tableViewType: TableViewType) => {
  const { setSelectedTableViewId, recordAppliedTableView, onTableViewDeleted, clearTableSessionState } =
    useTableSessionState(tableViewType);
  const { checkForTableViewChanges, notifyIfTableViewUpdated } = useTableViewChangeCheck(tableViewType);
  const { apiCall: getDefaultTableViewIdApiCall } = useEvolveGet('admin/userPreference/:preferenceKey');
  const { apiCall: getTableViewApiCall } = useEvolveGet('admin/tableView/:tableViewId');
  const { apiCall: deleteDefaultTableViewIdApiCall } = useEvolveDelete('admin/userPreference/:preferenceKey');

  const [loadingInitialTableView, setLoadingInitialTableView] = useState(false);

  const getDefaultTableViewId = useCallback(
    async () =>
      await getDefaultTableViewIdApiCall({
        url: `admin/userPreference/DEFAULT_TABLE_VIEW_ID_${tableViewType}`,
      })
        .then((userPreference) => {
          if (isNil(userPreference)) return null;
          return parseUserPreferenceValueJson<`DEFAULT_TABLE_VIEW_ID_${TableViewType}`>(userPreference.preferenceValue);
        })
        .catch(() => null), // Assume the user has no default table view.
    [tableViewType, getDefaultTableViewIdApiCall],
  );

  const getTableView = useCallback(
    async (tableViewId: TableViewId) =>
      await getTableViewApiCall({
        url: `admin/tableView/${tableViewId}`,
      }).catch(() => null), // Assume the table view doesn't exist.
    [getTableViewApiCall],
  );

  const getDefaultTableView = useCallback(async () => {
    const defaultTableViewId = await getDefaultTableViewId();
    if (!defaultTableViewId) return null;
    return await getTableView(defaultTableViewId);
  }, [getDefaultTableViewId, getTableView]);

  const reloadWithDefaultTableView = useCallback(() => {
    clearTableSessionState();
    window.location.reload();
  }, [clearTableSessionState]);

  const handleBackgroundUpdateCache = useCallback(
    async (gridApi: GridApi) => {
      const cachedDefaultTableView = getLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
      if (isNil(cachedDefaultTableView)) return;
      const latestDefaultTableViewId = await getDefaultTableViewId();
      if (!latestDefaultTableViewId) {
        // [Stale cache] User has removed their default table view preference.
        setLocalStorageWrapped(`USER_PREFERENCE_DEFAULT_TABLE_VIEW_ID_${tableViewType}`, null);
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
        notifications.show({
          title: 'Default table view removed',
          message: (
            <>
              Your default table view has been removed.{' '}
              <Text className="link" span onClick={reloadWithDefaultTableView}>
                Click here
              </Text>{' '}
              to reload.
            </>
          ),
        });
        return;
      }

      const latestDefaultTableView = await getTableView(latestDefaultTableViewId);
      if (!latestDefaultTableView) {
        // [Stale cache] User's default table view has been deleted or is inaccessible.
        void deleteDefaultTableViewIdApiCall({
          url: `admin/userPreference/DEFAULT_TABLE_VIEW_ID_${tableViewType}`,
        });
        setLocalStorageWrapped(`USER_PREFERENCE_DEFAULT_TABLE_VIEW_ID_${tableViewType}`, null);
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
        onTableViewDeleted(cachedDefaultTableView.tableViewId);
        notifications.show({
          title: 'Default table view deleted',
          message: 'Your default table view has been deleted.',
        });
      } else if (latestDefaultTableViewId !== cachedDefaultTableView.tableViewId) {
        // [Stale cache] User has a different default table view.
        setLocalStorageWrapped(`USER_PREFERENCE_DEFAULT_TABLE_VIEW_ID_${tableViewType}`, latestDefaultTableViewId);
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, latestDefaultTableView);
        notifications.show({
          title: 'Default table view changed',
          message: (
            <>
              Your default table view has changed.{' '}
              <Text className="link" span onClick={reloadWithDefaultTableView}>
                Click here
              </Text>{' '}
              to reload.
            </>
          ),
        });
      } else if (latestDefaultTableView.gridState !== cachedDefaultTableView.gridState) {
        // [Stale cache] User's default table view has been updated.
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, latestDefaultTableView);
        notifyIfTableViewUpdated(gridApi, latestDefaultTableView);
      }
    },
    [
      tableViewType,
      getDefaultTableViewId,
      getTableView,
      deleteDefaultTableViewIdApiCall,
      reloadWithDefaultTableView,
      onTableViewDeleted,
      notifyIfTableViewUpdated,
    ],
  );

  const restoreTableSessionState = useCallback(
    async (gridApi: GridApi, { selectedTableViewId, savedGridState, gridState }: TableSessionState) => {
      if (isNil(selectedTableViewId) || isNotNil(savedGridState)) {
        if (isNotNil(gridState)) loadSavedGridState(gridState, gridApi);
        if (isNotNil(selectedTableViewId)) void checkForTableViewChanges(gridApi, selectedTableViewId);
        return;
      }
      // The user navigated away before their selected table view finished loading.
      const tableView = await getTableView(selectedTableViewId);
      if (isNil(tableView)) {
        setSelectedTableViewId(null);
        return;
      }
      recordAppliedTableView(selectedTableViewId, tableView.gridState);
      loadSavedGridState(tableView.gridState, gridApi);
    },
    [getTableView, checkForTableViewChanges, setSelectedTableViewId, recordAppliedTableView],
  );

  const restoreDefaultTableView = useCallback(
    async (gridApi: GridApi) => {
      const cachedDefaultTableView = getLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
      const defaultTableView = cachedDefaultTableView ?? (await getDefaultTableView());
      if (isNil(cachedDefaultTableView)) {
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, defaultTableView ?? null);
      }
      setSelectedTableViewId(defaultTableView?.tableViewId ?? null);
      if (isNil(defaultTableView)) return;
      recordAppliedTableView(defaultTableView.tableViewId, defaultTableView.gridState);
      loadSavedGridState(defaultTableView.gridState, gridApi);
      // The cached default table view may be out of date.
      if (isNotNil(cachedDefaultTableView)) {
        void handleBackgroundUpdateCache(gridApi);
      }
    },
    [tableViewType, getDefaultTableView, setSelectedTableViewId, recordAppliedTableView, handleBackgroundUpdateCache],
  );

  /** Applies the grid state the table should start with. The default table view only applies to the first visit of the session. */
  const restoreInitialTableView = useCallback(
    async (gridApi: GridApi) => {
      if (tableViewType === TABLE_VIEW_TYPE.UNKNOWN) return;
      try {
        setLoadingInitialTableView(true);
        const tableSessionState = getTableSessionState(tableViewType);
        if (isNotNil(tableSessionState)) {
          await restoreTableSessionState(gridApi, tableSessionState);
        } else {
          await restoreDefaultTableView(gridApi);
        }
      } finally {
        setLoadingInitialTableView(false);
      }
    },
    [tableViewType, restoreTableSessionState, restoreDefaultTableView],
  );

  return { restoreInitialTableView, loadingInitialTableView };
};
